import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Server, User, Lock, Loader2, Download, Film } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { BRAND } from "@/lib/brand";
import { getLastServer } from "@/lib/store";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [server, setServer] = useState(() => getLastServer() || BRAND.defaultServer || "");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!server || !username || !password) {
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);
    try {
      await login({ server, username, password });
      toast.success("Connected");
      navigate("/");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not sign in. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden px-4 py-10">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1688678004647-945d5aaf91c1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600')",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#05070A] via-[#05070A]/85 to-[#05070A]/70" />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="relative w-full max-w-md"
      >
        <div className="flex items-center gap-2 mb-8 justify-center">
          {BRAND.logoUrl ? (
            <img src={BRAND.logoUrl} alt={BRAND.name} className="h-10 sm:h-12 w-auto" />
          ) : (
            <>
              <div className="h-11 w-11 rounded-xl bg-[#14A3FF] flex items-center justify-center shadow-[0_0_25px_rgba(20,163,255,0.5)]">
                <Play className="h-6 w-6 text-white fill-white" />
              </div>
              <span className="text-2xl font-extrabold tracking-tight text-white font-heading">
                {BRAND.name}
              </span>
            </>
          )}
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#0C1017]/80 backdrop-blur-xl p-7 shadow-2xl">
          <h1 className="text-2xl font-bold text-white font-heading mb-1">Sign in to your panel</h1>
          <p className="text-sm text-slate-400 mb-6">
            Enter your username and password to browse your library.
          </p>

          <form onSubmit={submit} className="space-y-4">
            {!BRAND.defaultServer && (
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-xs uppercase tracking-widest">Server address</Label>
                <div className="relative">
                  <Server className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    data-testid="server-url-input"
                    value={server}
                    onChange={(e) => setServer(e.target.value)}
                    placeholder="http://your-panel.com:8080"
                    className="pl-9 bg-[#131924] border-white/10 text-white placeholder:text-slate-600 h-11"
                  />
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs uppercase tracking-widest">Username</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <Input
                  data-testid="username-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="your username"
                  className="pl-9 bg-[#131924] border-white/10 text-white placeholder:text-slate-600 h-11"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs uppercase tracking-widest">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <Input
                  data-testid="password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-9 bg-[#131924] border-white/10 text-white placeholder:text-slate-600 h-11"
                />
              </div>
            </div>

            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-[#14A3FF] hover:bg-[#0E8FE6] text-white font-semibold active:scale-95 transition-transform"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign in"}
            </Button>
          </form>
        </div>

        <div className="flex items-center justify-center gap-6 mt-6 text-slate-500 text-xs">
          <span className="flex items-center gap-1.5"><Film className="h-3.5 w-3.5" /> Movies</span>
          <span className="flex items-center gap-1.5"><Play className="h-3.5 w-3.5" /> Series</span>
          <span className="flex items-center gap-1.5"><Download className="h-3.5 w-3.5" /> Downloads</span>
        </div>
      </motion.div>
    </div>
  );
}
